import React, { useEffect, useState } from 'react'
import { Flex, Anchor, Button, ContactUsModal, BusinessContactModal } from '../../lib/components'
import { useBreakpoint } from '../../lib/utils/hooks'
import { useNavigate } from 'react-router-dom'
import './about-us-v2.scss'
import { MD } from '../../lib/utils/constants'

const leadershipMembers = [
  {
    id: 'amardeep',
    name: 'Amardeep Sharma',
    image: 'https://credibila-assets.s3.amazonaws.com/images-v2/about-us-img-amardeep.png',
  },
  {
    id: 'karan',
    name: 'Karan Rai',
    image: 'https://credibila-assets.s3.amazonaws.com/images-v2/about-us-img-karan.png',
  },
  {
    id: 'thomas',
    name: 'Thomas Karsten Beute',
    image: 'https://credibila-assets.s3.amazonaws.com/images-v2/about-us-img-thomas.png',
  },
  {
    id: 'jalpesh',
    name: 'Dr. Jalpesh Mehta',
    image:
      'https://credibila-assets.s3.ap-south-1.amazonaws.com/images-v2/about-us-img-jalpesh.png',
  },
  {
    id: 'amit',
    name: 'Amit Saxena',
    image: 'https://credibila-assets.s3.ap-south-1.amazonaws.com/images-v2/about-us-img-amit.png',
  },
]

const AboutUsV2 = () => {
  const { breakpoint } = useBreakpoint()
  const [selectedFounder, setSelectedFounder] = useState<string | null>(null)
  const [showContactUsModal, setShowContactUsModal] = useState(false)
  const [showBusinessContactModal, setShowBusinessContactModal] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    if (selectedFounder) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'auto'
    }
  }, [selectedFounder])

  const handleCardKeyDown = (founderId: string, event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      setSelectedFounder(founderId)
    }
  }

  return (
    <div className='about-us-v2'>
      {/* About us cover section */}
      <section className='about-us-hero-section' aria-label='About Credibila'>
        <div className='about-us-hero-content'>
          <h1 className='about-us-hero-title'>Built on Trust</h1>
        </div>
      </section>

      {/* Private markets section */}
      <section className='about-us-v2-access-section' aria-label='Private markets access'>
        <div className='about-us-v2-access-container' data-testid='about-us-private-markets'>
          <h2 className='about-us-v2-access-title'>
            Private markets weren&apos;t built for exclusivity
            <br />
            they drifted into it over time
          </h2>
          <p className='about-us-v2-access-description'>
            Access to private opportunities has long been limited by opacity, fragmented regulation
            & information asymmetry.
          </p>
          <Anchor
            href='/faq'
            text="How we're opening access →"
            variant='basic-anchor'
            classes={['about-us-v2-access-link']}
          />
        </div>
      </section>

      {/* Regulated bridge section */}
      <section className='about-us-v2-bridge-section' aria-label='Regulated bridge section'>
        <div className='about-us-v2-bridge-container' data-testid='about-us-regulated-bridge'>
          <div className='about-us-v2-bridge-intro'>
            <h2 className='about-us-v2-bridge-title'>
              A Regulated Bridge,&nbsp;
              <span className='about-us-v2-bridge-title-accent'>Proven</span>
            </h2>
            <div className='about-us-v2-bridge-copy'>
              <p className='about-us-v2-bridge-description'>
                Credibila serves as a regulated bridge between clients & issuers, providing
                transparent & governed access to private market opportunities across jurisdictions.
                Our platform applies disciplined due diligence, structured risk oversight &
                continuous monitoring-an approach reflected in our growing global footprint, active
                opportunities & the capital deployed through Credibila to date.
              </p>
              <div className='about-us-v2-bridge-stats'>
                <div className='about-us-v2-bridge-stat'>
                  <span className='about-us-v2-bridge-stat-value'>6+</span>
                  <span className='about-us-v2-bridge-stat-unit'>Countries</span>
                  <span className='about-us-v2-bridge-stat-label'>Global Presence</span>
                </div>
                <div className='about-us-v2-bridge-stat'>
                  <span className='about-us-v2-bridge-stat-value'>3000+</span>
                  <span className='about-us-v2-bridge-stat-unit'>Strong Community</span>
                </div>
                <div className='about-us-v2-bridge-stat'>
                  <span className='about-us-v2-bridge-stat-value'>80+</span>
                  <span className='about-us-v2-bridge-stat-unit'>Years</span>
                  <span className='about-us-v2-bridge-stat-label'>Combined Team Experience</span>
                </div>
                <div className='about-us-v2-bridge-stat'>
                  <span className='about-us-v2-bridge-stat-value'>30 M +</span>
                  <span className='about-us-v2-bridge-stat-unit'>AUM</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How we build trust section */}
      <section className='about-us-v2-trust-section' aria-label='How we build trust'>
        <div className='about-us-v2-trust-inner' data-testid='about-us-trust'>
          <h2 className='about-us-v2-trust-heading'>How We Build Trust</h2>
          <p className='about-us-v2-trust-subheading'>
            Through disciplined due diligence, structured risk oversight & continuous monitoring,
            Credibila applies a consistent process to every opportunity on the platform.
          </p>

          <div className='about-us-v2-trust-steps'>
            <div className='about-us-v2-trust-step'>
              <div className='about-us-v2-trust-step-marker'>01</div>
              <h3 className='about-us-v2-trust-step-title'>Evaluate & Approve</h3>
              <p className='about-us-v2-trust-step-copy'>
                Each opportunity is independently vetted by our diligence & advisory partners; only
                approved opportunities proceed to clients.
              </p>
            </div>

            <div className='about-us-v2-trust-step'>
              <div className='about-us-v2-trust-step-marker'>02</div>
              <h3 className='about-us-v2-trust-step-title'>Review & Invest</h3>
              <p className='about-us-v2-trust-step-copy'>
                Approved deals include structured data rooms and key metrics for client analysis;
                investments are completed digitally via secure payment & custody channels.
              </p>
            </div>

            <div className='about-us-v2-trust-step'>
              <div className='about-us-v2-trust-step-marker'>03</div>
              <h3 className='about-us-v2-trust-step-title'>Monitor & Report</h3>
              <p className='about-us-v2-trust-step-copy'>
                Clients receive periodic updates & clear performance tracking through transparent
                reporting & custodial safeguards.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership section */}
      <section className='about-us-v2-leadership-section' aria-label='Leadership behind Credibila'>
        <div className='about-us-v2-leadership-inner'>
          <div className='about-us-v2-leadership-head'>
            <h2 className='about-us-v2-leadership-title'>Leadership Behind Credibila</h2>
            <div className='about-us-v2-leadership-copy-wrap'>
              <p className='about-us-v2-leadership-copy'>
                Credibila is led by a global team of financial experts, legal advisors &
                technologists who believe private markets should be as transparent as public ones.
              </p>
              <p className='about-us-v2-leadership-copy' style={{ marginTop: '0' }}>
                Our leadership combines decades of experience in banking, asset management & fintech
                innovation.
              </p>
            </div>
          </div>
          <div className='about-us-v2-leadership-grid' aria-label='Leadership members'>
            {leadershipMembers.map((member) => (
              <div
                key={member.id}
                className='about-us-v2-leadership-card'
                role='button'
                tabIndex={0}
                aria-label={`View ${member.name} profile`}
                onClick={() => setSelectedFounder(member.id)}
                onKeyDown={(event) => handleCardKeyDown(member.id, event)}
                data-testid={`founder-card-${member.id}`}
              >
                <div className='about-us-v2-leadership-media'>
                  <img
                    src={member.image}
                    alt={member.name}
                    className='about-us-v2-leadership-image'
                  />
                </div>
                <div className='about-us-v2-leadership-name'>{member.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Legacy founders section kept hidden in V2 */}
      <div className='about-us-founders-section about-us-v2-hidden-section'>
        <Flex
          classes={[
            'about-us-founders-container',
            `${breakpoint && breakpoint <= MD ? 'column' : ''}`,
          ]}
        >
          <div
            className='about-us-founder-card'
            onClick={() => setSelectedFounder('amardeep')}
            data-testid='founder-card-amardeep'
          >
            <img
              src='https://credibila-assets.s3.amazonaws.com/images-v2/about-us-img-amardeep.png'
              alt='Amardeep Sharma'
              className='about-us-founder-image'
            />
            <div className='about-us-founder-label'>
              <div className='about-us-founder-name'>Amardeep Sharma</div>
            </div>
          </div>

          <div
            className='about-us-founder-card'
            onClick={() => setSelectedFounder('karan')}
            data-testid='founder-card-karan'
          >
            <img
              src='https://credibila-assets.s3.amazonaws.com/images-v2/about-us-img-karan.png'
              alt='Karan Rai'
              className='about-us-founder-image'
            />
            <div className='about-us-founder-label'>
              <div className='about-us-founder-name'>Karan Rai</div>
            </div>
          </div>

          <div
            className='about-us-founder-card'
            onClick={() => setSelectedFounder('thomas')}
            data-testid='founder-card-thomas'
          >
            <img
              src='https://credibila-assets.s3.amazonaws.com/images-v2/about-us-img-thomas.png'
              alt='Thomas Karsten Beute'
              className='about-us-founder-image'
            />
            <div className='about-us-founder-label'>
              <div className='about-us-founder-name'>Thomas Karsten Beute</div>
            </div>
          </div>

          <div
            className='about-us-founder-card'
            onClick={() => setSelectedFounder('jalpesh')}
            data-testid='founder-card-jalpesh'
          >
            <img
              src='https://credibila-assets.s3.ap-south-1.amazonaws.com/images-v2/about-us-img-jalpesh.png'
              alt='Dr. Jalpesh Mehta'
              className='about-us-founder-image'
            />
            <div className='about-us-founder-label'>
              <div className='about-us-founder-name'>Dr. Jalpesh Mehta</div>
            </div>
          </div>
        </Flex>
      </div>

      {/* Founder Modal */}
      {selectedFounder && (
        <div className='about-us-modal-overlay' onClick={() => setSelectedFounder(null)}>
          <div className='about-us-modal' onClick={(e) => e.stopPropagation()}>
            <button
              className='about-us-modal-close'
              onClick={() => setSelectedFounder(null)}
              data-testid='modal-close'
            >
              ×
            </button>
            {selectedFounder === 'amardeep' && (
              <div className='about-us-modal-content'>
                <Flex classes={['gap-60', 'flex-align-center']}>
                  <img
                    src='https://credibila-assets.s3.amazonaws.com/images-v2/about-us-img-amardeep.png'
                    alt='Amardeep Sharma'
                    className='about-us-modal-image'
                  />
                  <div>
                    <Flex classes={['flex-align-center', 'mb-24']}>
                      <h3 className='about-us-modal-title'>
                        <strong>Amardeep Sharma</strong>
                      </h3>
                      <img
                        src='https://credibila-assets.s3.amazonaws.com/opportunity-images/team-linkedIn.png'
                        alt='linkedin'
                        className='about-us-linkedin'
                        onClick={() =>
                          window.open(
                            'https://www.linkedin.com/in/amardeep-sharma-b150926?originalSubdomain=ae',
                          )
                        }
                      />
                    </Flex>
                    <p className='about-us-modal-text'>
                      An alumnus of the University of Oxford with more than two decades of
                      experience in capital raising, investment banking, asset management, M&A, fund
                      management, trade finance, structured products solutions, and renewable energy
                      consulting & advisory, Mr. Amardeep Sharma, was honoured at the 24th Edition
                      of the Asian Business and Social Forum: Greatest Brands and Leaders 2024 by
                      AsiaOne Magazine. A distinguished leader in the financial sector, known for
                      his strategic vision and expertise in investment management, Mr. Sharma has
                      been driving success by delivering innovative financial solutions and
                      fostering sustainable growth across various industries. In recognition of his
                      exemplary leadership, being a trusted partner for businesses and clients,
                      fostering long-term value creation through strategic investments and sound
                      financial planning, he was honored with the prestigious Global Indian of the
                      Year 2023-24 Award.
                    </p>
                  </div>
                </Flex>
              </div>
            )}
            {selectedFounder === 'karan' && (
              <div className='about-us-modal-content'>
                <Flex classes={['gap-60', 'flex-align-center']}>
                  <img
                    src='https://credibila-assets.s3.amazonaws.com/images-v2/about-us-img-karan.png'
                    alt='Karan Rai'
                    className='about-us-modal-image'
                  />
                  <div>
                    <Flex classes={['flex-align-center', 'mb-24']}>
                      <h3 className='about-us-modal-title'>
                        <strong>Karan Rai</strong>
                      </h3>
                      <img
                        src='https://credibila-assets.s3.amazonaws.com/opportunity-images/team-linkedIn.png'
                        alt='linkedin'
                        className='about-us-linkedin'
                        onClick={() => window.open('https://www.linkedin.com/in/karanrai')}
                      />
                    </Flex>
                    <p className='about-us-modal-text'>
                      Mr. Karan Rai, an alumnus of the University of Oxford, brings over a decade of
                      experience in technology entrepreneurship and product development. As the
                      founder and CEO of Launch Ventures, he has been instrumental in creating
                      high-performance teams that deliver innovative technology solutions for
                      startups and established enterprises. Known for his commitment to addressing
                      global challenges, Karan has led initiatives focused on climate change and
                      misinformation. His leadership is characterized by a collaborative approach
                      that empowers teams to build meaningful products, positioning Launch Ventures
                      as a key player in the technology landscape.
                    </p>
                  </div>
                </Flex>
              </div>
            )}
            {selectedFounder === 'thomas' && (
              <div className='about-us-modal-content'>
                <Flex classes={['gap-60', 'flex-align-center']}>
                  <img
                    src='https://credibila-assets.s3.amazonaws.com/images-v2/about-us-img-thomas.png'
                    alt='Thomas Karsten Beute'
                    className='about-us-modal-image'
                  />
                  <div>
                    <Flex classes={['flex-align-center', 'mb-24']}>
                      <h3 className='about-us-modal-title'>
                        <strong>Thomas Karsten Beute</strong>
                      </h3>
                    </Flex>
                    <p className='about-us-modal-text'>
                      Mr. Thomas Karsten Beute, a lawyer by qualification, has over a decade of
                      international experience in wealth management, investment advisory &
                      cross-border financial structuring. He has served as Director at Julius Baer,
                      where he led wealth planning, legal advisory & fund structuring initiatives
                      for ultra-high net worth individuals. He has also held senior positions across
                      Amicorp Group, E&Y & PwC. His extensive industry experience & strategic
                      insights help Credibila create long-term value for its stakeholders.
                    </p>
                  </div>
                </Flex>
              </div>
            )}
            {selectedFounder === 'jalpesh' && (
              <div className='about-us-modal-content'>
                <Flex classes={['gap-60', 'flex-align-center']}>
                  <img
                    src='https://credibila-assets.s3.ap-south-1.amazonaws.com/images-v2/about-us-img-jalpesh.png'
                    alt='Dr. Jalpesh Mehta'
                    className='about-us-modal-image'
                  />
                  <div>
                    <Flex classes={['flex-align-center', 'mb-24']}>
                      <h3 className='about-us-modal-title'>
                        <strong>Dr. Jalpesh Mehta</strong>
                      </h3>
                      <img
                        src='https://credibila-assets.s3.amazonaws.com/opportunity-images/team-linkedIn.png'
                        alt='linkedin'
                        className='about-us-linkedin'
                        onClick={() =>
                          window.open('https://www.linkedin.com/in/jalpeshmehta/', '_blank')
                        }
                      />
                    </Flex>
                    <p className='about-us-modal-text'>
                      Dr. Jalpesh Mehta holds an MBA and a Ph.D. in Management Studies (Strategy)
                      from NMIMS University, Mumbai. He has over 25 years of experience across
                      corporate banking, private banking, NRI services, retail assets, and consumer
                      banking. He has held senior leadership roles at ICICI Bank, India, and has
                      been instrumental in setting up the NRI and international business in 11
                      countries across the Middle East, Asia Pacific, and Europe. As the chairperson
                      of IBAF (India Business Advisory Forum) and Mentor, AIM, NITI Aayog,
                      Government of India, Dr. Mehta has been inspiring entrepreneurs and helping
                      scale businesses.
                    </p>
                  </div>
                </Flex>
              </div>
            )}
            {selectedFounder === 'amit' && (
              <div className='about-us-modal-content'>
                <Flex classes={['gap-60', 'flex-align-center']}>
                  <img
                    src='https://credibila-assets.s3.ap-south-1.amazonaws.com/images-v2/about-us-img-amit.png'
                    alt='Amit Saxena'
                    className='about-us-modal-image'
                  />
                  <div>
                    <Flex classes={['flex-align-center', 'mb-24']}>
                      <h3 className='about-us-modal-title'>
                        <strong>Amit Saxena</strong>
                      </h3>
                    </Flex>
                    <p className='about-us-modal-text'>
                      Mr. Amit is a financial markets professional with over 18 years of experience
                      across corporate banking, private banking, and wealth management. He began his
                      career with Citigroup in Singapore and went on to hold key roles at Credit
                      Suisse in Dubai and Barclays Wealth in the UK, where he built a strong client
                      franchise serving Ultra High Net Worth global NRI clients.
                    </p>
                    <p className='about-us-modal-text'>
                      Amit specializes in structured finance and tailored wealth management
                      solutions, bringing deep market insight and expertise in corporate and
                      investment banking.
                    </p>
                    <p className='about-us-modal-text'>
                      He is developing multi-family strategic partnerships across London, Geneva,
                      and Dubai.
                    </p>
                  </div>
                </Flex>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Legacy leadership + cards sections hidden in V2 */}
      <div className='about-us-leadership-section about-us-v2-hidden-section' />

      {/* Who We Serve section */}
      <section className='about-us-v2-serve-section' aria-label='Who we serve'>
        <div className='about-us-v2-serve-inner'>
          <h2 className='about-us-v2-serve-heading'>Who We Serve</h2>
          <div className='about-us-v2-serve-grid'>
            <div className='about-us-v2-serve-card about-us-v2-serve-card--clients'>
              <div className='about-us-v2-serve-card-header'>
                <div className='about-us-v2-serve-card-icon-badge' aria-hidden='true'>
                  <img
                    src='/assets/about-us/serve-user.svg'
                    alt=''
                    className='about-us-v2-serve-card-badge-icon'
                  />
                </div>
                <h3 className='about-us-v2-serve-card-title'>For Clients</h3>
              </div>
              <ul className='about-us-v2-serve-card-list'>
                <li className='about-us-v2-serve-card-item'>
                  <img
                    src='/assets/about-us/serve-check-clients.svg'
                    alt=''
                    className='about-us-v2-serve-card-check'
                    aria-hidden='true'
                  />
                  Transparent access to private markets
                </li>
                <li className='about-us-v2-serve-card-item'>
                  <img
                    src='/assets/about-us/serve-check-clients.svg'
                    alt=''
                    className='about-us-v2-serve-card-check'
                    aria-hidden='true'
                  />
                  Structured evaluation and ongoing oversight
                </li>
                <li className='about-us-v2-serve-card-item'>
                  <img
                    src='/assets/about-us/serve-check-clients.svg'
                    alt=''
                    className='about-us-v2-serve-card-check'
                    aria-hidden='true'
                  />
                  Clear information to support informed decisions
                </li>
              </ul>
              <Anchor
                href='/signup'
                text='Create Account →'
                variant='basic-anchor'
                classes={['about-us-v2-serve-card-cta', 'about-us-v2-serve-card-cta--solid']}
              />
            </div>

            <div className='about-us-v2-serve-card about-us-v2-serve-card--issuers'>
              <div className='about-us-v2-serve-card-header'>
                <div className='about-us-v2-serve-card-icon-badge' aria-hidden='true'>
                  <img
                    src='/assets/about-us/serve-building.svg'
                    alt=''
                    className='about-us-v2-serve-card-badge-icon'
                  />
                </div>
                <h3 className='about-us-v2-serve-card-title'>For Issuers</h3>
              </div>
              <ul className='about-us-v2-serve-card-list'>
                <li className='about-us-v2-serve-card-item'>
                  <img
                    src='/assets/about-us/serve-check-issuers.svg'
                    alt=''
                    className='about-us-v2-serve-card-check'
                    aria-hidden='true'
                  />
                  Regulated access to global capital
                </li>
                <li className='about-us-v2-serve-card-item'>
                  <img
                    src='/assets/about-us/serve-check-issuers.svg'
                    alt=''
                    className='about-us-v2-serve-card-check'
                    aria-hidden='true'
                  />
                  Credible market positioning
                </li>
                <li className='about-us-v2-serve-card-item'>
                  <img
                    src='/assets/about-us/serve-check-issuers.svg'
                    alt=''
                    className='about-us-v2-serve-card-check'
                    aria-hidden='true'
                  />
                  A compliance-first fundraising framework
                </li>
              </ul>
              <button
                type='button'
                className='about-us-v2-serve-card-cta about-us-v2-serve-card-cta--outline'
                onClick={() => setShowBusinessContactModal(true)}
              >
                Get in touch →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Future section */}
      <section className='about-us-v2-future-section' aria-label='The future'>
        <div className='about-us-v2-future-inner'>
          <h2 className='about-us-v2-future-heading'>
            The future of investing is transparent, trusted & borderless.
          </h2>
          <div className='about-us-v2-future-rule' aria-hidden='true' />
          <p className='about-us-v2-future-copy'>
            At Credibila, we are not just building a platform - we are shaping a more open financial
            ecosystem where clients everywhere can access, understand & participate in the
            opportunities that move the world forward.
          </p>
          <div className='about-us-v2-future-button-wrap'>
            <Button
              text='Explore the Opportunities'
              variant='basic-btn'
              handleClick={() => navigate('/opportunities')}
              classes={['about-us-v2-future-button']}
            />
          </div>
        </div>
      </section>

      {showContactUsModal && (
        <ContactUsModal
          active={showContactUsModal}
          handleOnClose={() => setShowContactUsModal(false)}
        />
      )}

      {showBusinessContactModal && (
        <BusinessContactModal
          active={showBusinessContactModal}
          handleOnClose={() => setShowBusinessContactModal(false)}
        />
      )}
    </div>
  )
}

export { AboutUsV2 }
